import type { ChecklistStep } from '../types';
import { Button } from '@/components/ui/button';
import { ButtonSpinner } from '@/components/shared/LoadingSpinner';

interface Props {
    step: ChecklistStep;
    onComplete: () => void;
    onBlock: () => void;
    isCompleting?: boolean;
}

const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
    not_started: { bg: 'bg-gray-50', text: 'text-gray-600', label: 'Not Started' },
    in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'In Progress' },
    completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Completed' },
    blocked: { bg: 'bg-red-50', text: 'text-red-700', label: 'Blocked' },
    skipped: { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Skipped' },
};

export function StepCard({ step, onComplete, onBlock, isCompleting }: Props) {
    const style = statusStyles[step.status] ?? statusStyles.not_started;
    const isOverdue =
        step.status !== 'completed' &&
        step.status !== 'skipped' &&
        new Date(step.due_date) < new Date();

    return (
        <article
            className={`rounded-lg border p-4 ${style.bg}`}
            aria-label={`Step ${step.order}: ${step.title}`}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                            {step.order}. {step.title}
                        </span>
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}
                            role="status"
                        >
                            {style.label}
                        </span>
                        {isOverdue && (
                            <span
                                className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600"
                                role="alert"
                            >
                                Overdue
                            </span>
                        )}
                    </div>

                    {step.description && (
                        <p className="mt-1 text-sm text-muted-foreground">
                            {step.description}
                        </p>
                    )}

                    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        <span>
                            Due: {new Date(step.due_date).toLocaleDateString('en-GB')}
                        </span>
                        {step.evidence_required && (
                            <span className="text-amber-600">
                                <span aria-hidden="true">\ud83d\udcce</span> Evidence required
                            </span>
                        )}
                    </div>

                    {step.notes && (
                        <p className="mt-2 rounded bg-white/50 p-2 text-xs italic">
                            {step.notes}
                        </p>
                    )}
                </div>

                {step.status !== 'completed' && step.status !== 'skipped' && (
                    <div className="ml-3 flex flex-col gap-1 sm:flex-row">
                        <Button
                            size="sm"
                            onClick={onComplete}
                            disabled={isCompleting}
                            aria-label={`Complete step: ${step.title}`}
                            aria-busy={isCompleting}
                        >
                            {isCompleting ? <ButtonSpinner /> : null}
                            Complete
                        </Button>
                        {step.status !== 'blocked' && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="text-red-500"
                                onClick={onBlock}
                                aria-label={`Block step: ${step.title}`}
                            >
                                Block
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </article>
    );
}